<!-- 特种煤运输监控 -->
<template>
  <div class="special-coal-page">
    <section class="filter-panel">
      <el-form :model="queryForm" size="small" label-width="76px" class="filter-form">
        <el-form-item label="统计时间">
          <el-date-picker
            v-model="dateRange"
            type="datetimerange"
            value-format="yyyy-MM-dd HH:mm:ss"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            :default-time="['00:00:00', '23:59:59']"
            unlink-panels
            clearable
          />
        </el-form-item>
        <el-form-item label="客户">
          <el-select
            v-model="queryForm.custNo"
            filterable
            remote
            clearable
            reserve-keyword
            :remote-method="loadCustomerOptions"
            :loading="customerLoading"
            placeholder="全部客户"
          >
            <el-option
              v-for="item in customerOptions"
              :key="item.id"
              :label="item.deptName"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="品种">
          <el-select
            v-model="queryForm.cargoNo"
            filterable
            remote
            clearable
            reserve-keyword
            :remote-method="loadCargoOptions"
            :loading="cargoLoading"
            placeholder="全部品种"
          >
            <el-option
              v-for="item in cargoOptions"
              :key="item.varNo"
              :label="item.varName"
              :value="item.varNo"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="车牌号">
          <el-input v-model.trim="queryForm.trainNumber" clearable placeholder="请输入车牌号" />
        </el-form-item>
        <el-form-item label="计划号">
          <el-input v-model.trim="queryForm.planNo" clearable placeholder="请输入计划号" />
        </el-form-item>
        <el-form-item class="filter-actions">
          <el-button type="primary" icon="el-icon-search" :loading="queryLoading" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
          <el-button icon="el-icon-printer" :loading="exportingPdf" @click="handleExportPdf">导出PDF</el-button>
        </el-form-item>
      </el-form>
      <div class="quick-range">
        <el-button
          v-for="item in quickRanges"
          :key="item.value"
          size="mini"
          :type="activeRange === item.value ? 'primary' : 'default'"
          plain
          @click="setQuickRange(item.value)"
        >
          {{ item.label }}
        </el-button>
      </div>
    </section>

    <el-alert
      v-if="dashboardError"
      class="state-alert"
      :title="dashboardError"
      type="error"
      show-icon
      :closable="false"
    />

    <section v-loading="dashboardLoading" class="summary-grid">
      <div v-for="card in summaryCards" :key="card.key" :class="['metric-card', card.accent]">
        <div class="metric-head">
          <span>{{ card.label }}</span>
          <span>{{ card.caption }}</span>
        </div>
        <div class="metric-value">
          {{ card.value }}<small>{{ card.unit }}</small>
        </div>
        <div class="metric-foot">{{ card.sub }}</div>
      </div>
    </section>

    <section class="main-grid">
      <div v-loading="dashboardLoading" class="panel trend-panel">
        <div class="panel-head">
          <span>特种煤发运趋势</span>
          <span>按重磅时间统计净重和车次</span>
        </div>
        <div ref="trendChart" class="chart-box" />
      </div>

      <div v-loading="dashboardLoading" class="panel insight-panel">
        <div class="panel-head">
          <span>智能洞察</span>
          <span>基于聚合数据生成</span>
        </div>
        <el-empty v-if="!insights.length" description="暂无洞察数据" :image-size="72" />
        <div v-else class="insight-list">
          <div v-for="(item, index) in insights" :key="index" class="insight-item">
            <div class="insight-no">{{ formatIndex(index) }}</div>
            <div class="insight-content">
              <div class="insight-title">{{ item.title }}</div>
              <div class="insight-text">{{ item.content }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-loading="dashboardLoading" class="rank-grid">
      <div class="panel">
        <div class="panel-head">
          <span>客户发运贡献 TOP5</span>
          <span>按净重排序</span>
        </div>
        <rank-list :list="customerTop" value-unit="吨" empty-text="暂无客户排行数据" />
      </div>
      <div class="panel">
        <div class="panel-head">
          <span>特种煤品类结构</span>
          <span>按净重占比</span>
        </div>
        <rank-list :list="cargoTop" value-unit="吨" empty-text="暂无品类排行数据" />
      </div>
    </section>

    <section class="table-panel">
      <div class="panel-head table-head">
        <span>运输明细追溯</span>
        <span>保留车牌、计划、客户、物资、磅单时间和重量字段</span>
      </div>
      <el-alert
        v-if="tableError"
        class="state-alert table-error"
        :title="tableError"
        type="error"
        show-icon
        :closable="false"
      />
      <el-table
        v-loading="tableLoading"
        :data="records"
        border
        stripe
        size="small"
        empty-text="当前筛选条件下暂无特种煤运输数据"
        style="width: 100%"
      >
        <el-table-column prop="trainNumber" label="车牌号" width="110" show-overflow-tooltip />
        <el-table-column prop="planNo" label="计划号" min-width="130" show-overflow-tooltip />
        <el-table-column label="客户" min-width="150" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.custName || row.custNo || '--' }}</template>
        </el-table-column>
        <el-table-column label="物资名称" min-width="130" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.cargoName || row.cargoNo || '--' }}</template>
        </el-table-column>
        <el-table-column prop="ingateTime" label="进门岗时间" width="155" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.ingateTime || '--' }}</template>
        </el-table-column>
        <el-table-column prop="emptyTime" label="空磅时间" width="155" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.emptyTime || '--' }}</template>
        </el-table-column>
        <el-table-column prop="heavyTime" label="重磅时间" width="155" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.heavyTime || '--' }}</template>
        </el-table-column>
        <el-table-column label="毛重(吨)" width="95" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.grossWeight) }}</template>
        </el-table-column>
        <el-table-column label="皮重(吨)" width="95" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.tareWeight) }}</template>
        </el-table-column>
        <el-table-column label="净重(吨)" width="95" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.netWeight) }}</template>
        </el-table-column>
        <el-table-column label="在厂时长" width="100" align="right">
          <template slot-scope="{ row }">{{ formatStay(row) }}</template>
        </el-table-column>
      </el-table>
      <div class="pagination-bar">
        <span>共 {{ page.total }} 条</span>
        <el-pagination
          background
          layout="sizes, prev, pager, next, jumper"
          :current-page="page.currentPage"
          :page-size="page.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="page.total"
          @current-change="handleCurrentChange"
          @size-change="handleSizeChange"
        />
      </div>
    </section>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import {
  fetchCargoOptions,
  fetchCustomerOptions,
  fetchSpecialCoalDashboard,
  getList
} from './api'

const RankList = {
  name: 'RankList',
  props: {
    list: {
      type: Array,
      default: () => []
    },
    valueUnit: {
      type: String,
      default: ''
    },
    emptyText: {
      type: String,
      default: '暂无数据'
    }
  },
  methods: {
    formatNumber(value) {
      const num = Number(value)
      if (value === null || value === undefined || value === '' || Number.isNaN(num)) return '--'
      return num.toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    widthOf(item) {
      const percent = Number(item.percent)
      if (!Number.isNaN(percent) && percent > 0) return `${Math.min(100, percent)}%`
      const max = this.list.reduce((result, row) => Math.max(result, Number(row.value) || 0), 0)
      const current = Number(item.value) || 0
      return max ? `${Math.max(6, (current / max) * 100)}%` : '0%'
    }
  },
  template: `
    <div class="rank-list">
      <el-empty v-if="!list.length" :description="emptyText" :image-size="68" />
      <div v-for="(item, index) in list" v-else :key="index" class="rank-row">
        <span class="rank-no">{{ index + 1 }}</span>
        <span class="rank-name" :title="item.name">{{ item.name || '--' }}</span>
        <span class="rank-value">{{ formatNumber(item.value) }}{{ valueUnit }}</span>
        <span class="rank-progress"><i :style="{ width: widthOf(item) }"></i></span>
      </div>
    </div>
  `
}

export default {
  name: 'SpecialCoalTransportMonitoring',
  components: { RankList },
  data() {
    return {
      queryForm: {
        custNo: '',
        cargoNo: '',
        trainNumber: '',
        planNo: ''
      },
      dateRange: [],
      activeRange: 'seven',
      quickRanges: [
        { label: '今日', value: 'today' },
        { label: '近7日', value: 'seven' },
        { label: '本月', value: 'month' }
      ],
      customerOptions: [],
      cargoOptions: [],
      customerLoading: false,
      cargoLoading: false,
      exportingPdf: false,
      dashboardLoading: false,
      tableLoading: false,
      dashboardError: '',
      tableError: '',
      dashboard: {},
      summaryCards: [],
      customerTop: [],
      cargoTop: [],
      insights: [],
      records: [],
      page: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      charts: {
        trend: null
      }
    }
  },
  computed: {
    queryLoading() {
      return this.dashboardLoading || this.tableLoading
    }
  },
  mounted() {
    this.setQuickRange('seven', false)
    this.loadCustomerOptions('')
    this.loadCargoOptions('')
    this.loadData()
    window.addEventListener('resize', this.resizeCharts)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts)
    this.disposeCharts()
  },
  methods: {
    async loadData() {
      this.loadDashboard()
      this.loadTable()
    },
    async loadDashboard() {
      this.dashboardLoading = true
      this.dashboardError = ''
      try {
        const data = this.unwrapResponse(await fetchSpecialCoalDashboard(this.buildQueryParams()))
        this.dashboard = data || {}
        this.applyDashboard(this.dashboard)
      } catch (e) {
        this.dashboard = {}
        this.applyDashboard({})
        this.dashboardError = '看板数据加载失败，请稍后重试'
      } finally {
        this.dashboardLoading = false
        this.$nextTick(() => this.renderTrendChart())
      }
    },
    async loadTable() {
      this.tableLoading = true
      this.tableError = ''
      try {
        const params = {
          ...this.buildQueryParams(),
          specialType: 1,
          businessType: 2,
          page: this.page.currentPage,
          size: this.page.pageSize
        }
        const data = this.unwrapResponse(await getList(params)) || {}
        this.records = data.records || data.list || []
        this.page.total = Number(data.total) || 0
      } catch (e) {
        this.records = []
        this.page.total = 0
        this.tableError = '明细数据加载失败，请稍后重试'
      } finally {
        this.tableLoading = false
      }
    },
    async loadCustomerOptions(keyword) {
      this.customerLoading = true
      try {
        const data = this.unwrapResponse(await fetchCustomerOptions(keyword ? { key: keyword } : {}))
        this.customerOptions = this.normalizeArray(data).map(item => ({
          id: item.id || item.deptId || item.value,
          deptName: item.deptName || item.name || item.label
        })).filter(item => item.id)
      } finally {
        this.customerLoading = false
      }
    },
    async loadCargoOptions(keyword) {
      this.cargoLoading = true
      try {
        const data = this.unwrapResponse(await fetchCargoOptions(keyword ? { varName: keyword } : {}))
        this.cargoOptions = this.normalizeArray(data).map(item => ({
          varNo: item.varNo || item.value,
          varName: item.varName || item.name || item.label
        })).filter(item => item.varNo)
      } finally {
        this.cargoLoading = false
      }
    },
    buildQueryParams() {
      const params = {}
      Object.keys(this.queryForm).forEach(key => {
        if (this.queryForm[key] !== '' && this.queryForm[key] !== null && this.queryForm[key] !== undefined) {
          params[key] = this.queryForm[key]
        }
      })
      if (this.dateRange && this.dateRange.length === 2) {
        params.startTime = this.dateRange[0]
        params.endTime = this.dateRange[1]
      }
      return params
    },
    handleSearch() {
      this.page.currentPage = 1
      this.loadData()
    },
    handleReset() {
      this.queryForm = {
        custNo: '',
        cargoNo: '',
        trainNumber: '',
        planNo: ''
      }
      this.setQuickRange('seven', false)
      this.page.currentPage = 1
      this.loadData()
    },
    handleExportPdf() {
      if (this.dashboardLoading || this.tableLoading) {
        this.$message.warning('数据加载中，请稍后导出')
        return
      }
      const win = window.open('', '_blank')
      if (!win) {
        this.$message.error('浏览器阻止了导出窗口，请允许弹窗后重试')
        return
      }
      this.exportingPdf = true
      this.$nextTick(() => {
        try {
          const html = this.buildPdfHtml()
          win.document.open()
          win.document.write(html)
          win.document.close()
          win.onload = () => {
            win.focus()
            win.print()
          }
        } catch (e) {
          this.$message.error('导出PDF失败，请稍后重试')
        } finally {
          this.exportingPdf = false
        }
      })
    },
    handleCurrentChange(currentPage) {
      this.page.currentPage = currentPage
      this.loadTable()
    },
    handleSizeChange(pageSize) {
      this.page.pageSize = pageSize
      this.page.currentPage = 1
      this.loadTable()
    },
    setQuickRange(type, reload = true) {
      this.activeRange = type
      const now = new Date()
      let start = new Date(now)
      if (type === 'today') {
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      } else if (type === 'month') {
        start = new Date(now.getFullYear(), now.getMonth(), 1)
      } else {
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6)
      }
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
      this.dateRange = [`${this.formatDate(start)} 00:00:00`, `${this.formatDate(end)} 23:59:59`]
      if (reload) this.handleSearch()
    },
    applyDashboard(data) {
      const summary = data.summary || data.kpi || {}
      this.summaryCards = [
        {
          key: 'totalNetWeight',
          label: '特种煤发运量',
          caption: '净重',
          value: this.formatNumber(this.pick(summary, ['totalNetWeight', 'netWeightTotal', 'totalWeight', 'shippedWeight'])),
          unit: '吨',
          sub: this.formatCompare(summary.netWeightCompare, '较前一周期'),
          accent: 'accent-blue'
        },
        {
          key: 'totalVehicles',
          label: '运输车辆数',
          caption: '车次',
          value: this.formatNumber(this.pick(summary, ['totalVehicles', 'vehicleCount', 'truckCount'])),
          unit: '车',
          sub: `单车均重 ${this.formatNumber(this.pick(summary, ['avgVehicleNetWeight', 'avgNetWeight']))} 吨`,
          accent: 'accent-green'
        },
        {
          key: 'avgStayMinutes',
          label: '平均在厂时长',
          caption: '效率',
          value: this.formatNumber(this.pick(summary, ['avgStayMinutes', 'avgPlantStayMinutes', 'avgDurationMinutes'])),
          unit: '分钟',
          sub: this.pick(summary, ['stayCompareText', 'efficiencyText']) || '按进出门岗或在厂秒数计算',
          accent: 'accent-orange'
        },
        {
          key: 'customerCount',
          label: '覆盖客户',
          caption: '贡献',
          value: this.formatNumber(this.pick(summary, ['customerCount', 'custCount'])),
          unit: '家',
          sub: `TOP3 贡献 ${this.formatPercent(this.pick(summary, ['top3CustomerRate', 'customerConcentration']))}`,
          accent: 'accent-cyan'
        },
        {
          key: 'cargoCount',
          label: '特种煤品类',
          caption: '结构',
          value: this.formatNumber(this.pick(summary, ['cargoCount', 'materialCount'])),
          unit: '类',
          sub: `主力品类占比 ${this.formatPercent(this.pick(summary, ['mainCargoRate', 'topCargoRate']))}`,
          accent: 'accent-red'
        }
      ]
      this.customerTop = this.normalizeRank(data.customerTop || data.customerRank || data.customerDistribution)
      this.cargoTop = this.normalizeRank(data.cargoTop || data.cargoRank || data.cargoDistribution)
      this.insights = this.normalizeInsights(data.insights || data.insightList)
    },
    normalizeRank(list) {
      return this.normalizeArray(list).slice(0, 5).map(item => ({
        name: item.name || item.label || item.dimensionName || item.custName || item.customerName || item.cargoName || item.varName,
        value: this.pick(item, ['value', 'netWeight', 'weight', 'totalNetWeight', 'totalWeight']),
        percent: this.pick(item, ['percent', 'percentage', 'rate', 'ratio'])
      }))
    },
    normalizeInsights(list) {
      return this.normalizeArray(list).slice(0, 4).map(item => {
        if (typeof item === 'string') {
          return { title: item, content: '可在明细中按客户、物资、车牌继续追溯。' }
        }
        return {
          title: item.title || item.name || '智能洞察',
          content: item.content || item.text || item.desc || item.description || '--'
        }
      })
    },
    renderTrendChart() {
      const el = this.$refs.trendChart
      if (!el) return
      if (!this.charts.trend) {
        this.charts.trend = echarts.init(el)
      }
      const trend = this.dashboard.trend || this.dashboard.trends || {}
      const rows = Array.isArray(trend) ? trend : (trend.items || trend.rows || trend.data || [])
      const xData = rows.map(item => item.statDate || item.date || item.day || item.time || item.name || item.label)
      const weightData = rows.map(item => Number(this.pick(item, ['netWeight', 'weight', 'value', 'totalWeight'])) || 0)
      const vehicleData = rows.map(item => Number(this.pick(item, ['vehicleCount', 'truckCount', 'count'])) || 0)
      const hasData = xData.length > 0
      this.charts.trend.setOption({
        color: ['#409eff', '#67c23a'],
        title: hasData ? null : {
          text: '暂无趋势数据',
          left: 'center',
          top: 'middle',
          textStyle: { color: '#909399', fontSize: 14, fontWeight: 400 }
        },
        tooltip: { trigger: 'axis' },
        grid: { left: 42, right: 42, top: 48, bottom: 32 },
        legend: {
          top: 6,
          right: 10,
          data: ['净重(吨)', '车次']
        },
        xAxis: {
          type: 'category',
          data: xData,
          axisLabel: { color: '#606266' },
          axisLine: { lineStyle: { color: '#dcdfe6' } }
        },
        yAxis: [
          { type: 'value', name: '吨', axisLabel: { color: '#606266' }, splitLine: { lineStyle: { color: '#edf2f7' } } },
          { type: 'value', name: '车', axisLabel: { color: '#606266' }, splitLine: { show: false } }
        ],
        series: [
          {
            name: '净重(吨)',
            type: 'bar',
            data: weightData,
            barMaxWidth: 28,
            itemStyle: { borderRadius: [4, 4, 0, 0] }
          },
          {
            name: '车次',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            data: vehicleData,
            symbolSize: 6
          }
        ]
      }, true)
    },
    disposeCharts() {
      Object.keys(this.charts).forEach(key => {
        if (this.charts[key]) {
          this.charts[key].dispose()
          this.charts[key] = null
        }
      })
    },
    resizeCharts() {
      Object.keys(this.charts).forEach(key => {
        if (this.charts[key]) this.charts[key].resize()
      })
    },
    buildPdfHtml() {
      const filterText = this.buildFilterText()
      const trendImage = this.charts.trend ? this.charts.trend.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' }) : ''
      const title = '特种煤运输监控看板'
      return `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>${title}</title>
            <style>
              * { box-sizing: border-box; }
              body { margin: 0; padding: 24px; color: #1f2937; font-family: "Microsoft YaHei", Arial, sans-serif; background: #fff; }
              h1 { margin: 0 0 8px; font-size: 22px; line-height: 30px; }
              .meta { margin-bottom: 16px; color: #606266; font-size: 12px; line-height: 20px; }
              .section { margin-top: 16px; page-break-inside: avoid; }
              .section-title { margin-bottom: 8px; font-size: 15px; font-weight: 700; }
              .summary { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; }
              .card { min-height: 86px; padding: 10px; border: 1px solid #dcdfe6; border-radius: 4px; }
              .label { color: #606266; font-size: 12px; }
              .value { margin-top: 8px; color: #111827; font-size: 22px; font-weight: 700; }
              .sub { margin-top: 6px; color: #909399; font-size: 12px; }
              .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
              .box { padding: 10px; border: 1px solid #dcdfe6; border-radius: 4px; }
              .chart { width: 100%; max-height: 280px; object-fit: contain; border: 1px solid #ebeef5; }
              .insight { margin-bottom: 8px; line-height: 20px; }
              table { width: 100%; border-collapse: collapse; table-layout: fixed; }
              th, td { border: 1px solid #dcdfe6; padding: 6px 8px; font-size: 12px; line-height: 18px; word-break: break-all; }
              th { background: #f5f7fa; color: #303133; }
              @page { size: A4 landscape; margin: 12mm; }
            </style>
          </head>
          <body>
            <h1>${title}</h1>
            <div class="meta">导出时间：${this.formatDateTime(new Date())}；筛选条件：${this.escapeHtml(filterText)}</div>
            ${this.renderSummaryHtml()}
            <div class="section">
              <div class="section-title">特种煤发运趋势</div>
              ${trendImage ? `<img class="chart" src="${trendImage}" />` : '<div class="box">暂无趋势图</div>'}
            </div>
            <div class="section grid">
              <div class="box">
                <div class="section-title">智能洞察</div>
                ${this.renderInsightsHtml()}
              </div>
              <div class="box">
                <div class="section-title">客户发运贡献 TOP5</div>
                ${this.renderRankTableHtml(this.customerTop, '客户')}
                <div class="section-title" style="margin-top:12px;">特种煤品类结构</div>
                ${this.renderRankTableHtml(this.cargoTop, '品类')}
              </div>
            </div>
            <div class="section">
              <div class="section-title">运输明细追溯（当前页）</div>
              ${this.renderRecordsTableHtml()}
            </div>
          </body>
        </html>
      `
    },
    buildFilterText() {
      const parts = []
      if (this.dateRange && this.dateRange.length === 2) parts.push(`统计时间 ${this.dateRange[0]} 至 ${this.dateRange[1]}`)
      const customer = this.customerOptions.find(item => item.id === this.queryForm.custNo)
      const cargo = this.cargoOptions.find(item => item.varNo === this.queryForm.cargoNo)
      if (this.queryForm.custNo) parts.push(`客户 ${customer ? customer.deptName : this.queryForm.custNo}`)
      if (this.queryForm.cargoNo) parts.push(`品种 ${cargo ? cargo.varName : this.queryForm.cargoNo}`)
      if (this.queryForm.trainNumber) parts.push(`车牌号 ${this.queryForm.trainNumber}`)
      if (this.queryForm.planNo) parts.push(`计划号 ${this.queryForm.planNo}`)
      return parts.length ? parts.join('；') : '全部'
    },
    renderSummaryHtml() {
      const cards = this.summaryCards.map(card => `
        <div class="card">
          <div class="label">${this.escapeHtml(card.label)} / ${this.escapeHtml(card.caption)}</div>
          <div class="value">${this.escapeHtml(card.value)}${this.escapeHtml(card.unit)}</div>
          <div class="sub">${this.escapeHtml(card.sub)}</div>
        </div>
      `).join('')
      return `<div class="section"><div class="section-title">核心指标</div><div class="summary">${cards}</div></div>`
    },
    renderInsightsHtml() {
      if (!this.insights.length) return '<div class="insight">暂无洞察数据</div>'
      return this.insights.map((item, index) => `
        <div class="insight"><strong>${this.formatIndex(index)} ${this.escapeHtml(item.title)}</strong><br>${this.escapeHtml(item.content)}</div>
      `).join('')
    },
    renderRankTableHtml(list, label) {
      if (!list.length) return `<div class="insight">暂无${label}排行数据</div>`
      const rows = list.map((item, index) => `
        <tr>
          <td style="width:50px;">${index + 1}</td>
          <td>${this.escapeHtml(item.name || '--')}</td>
          <td style="width:120px;text-align:right;">${this.escapeHtml(this.formatNumber(item.value))} 吨</td>
        </tr>
      `).join('')
      return `<table><thead><tr><th style="width:50px;">排名</th><th>${label}</th><th style="width:120px;">净重</th></tr></thead><tbody>${rows}</tbody></table>`
    },
    renderRecordsTableHtml() {
      if (!this.records.length) return '<div class="box">当前筛选条件下暂无特种煤运输数据</div>'
      const rows = this.records.map(row => `
        <tr>
          <td>${this.escapeHtml(row.trainNumber || '--')}</td>
          <td>${this.escapeHtml(row.planNo || '--')}</td>
          <td>${this.escapeHtml(row.custName || row.custNo || '--')}</td>
          <td>${this.escapeHtml(row.cargoName || row.cargoNo || '--')}</td>
          <td>${this.escapeHtml(row.heavyTime || '--')}</td>
          <td style="text-align:right;">${this.escapeHtml(this.formatNumber(row.netWeight))}</td>
          <td style="text-align:right;">${this.escapeHtml(this.formatStay(row))}</td>
        </tr>
      `).join('')
      return `
        <table>
          <thead><tr><th>车牌号</th><th>计划号</th><th>客户</th><th>物资名称</th><th>重磅时间</th><th>净重(吨)</th><th>在厂时长</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      `
    },
    escapeHtml(value) {
      return String(value === undefined || value === null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    unwrapResponse(res) {
      const body = res && res.data !== undefined ? res.data : res
      if (body && body.data !== undefined) return body.data
      return body
    },
    normalizeArray(data) {
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.records)) return data.records
      if (data && Array.isArray(data.rows)) return data.rows
      if (data && Array.isArray(data.list)) return data.list
      if (data && Array.isArray(data.data)) return data.data
      return []
    },
    pick(target, keys) {
      if (!target) return undefined
      for (let i = 0; i < keys.length; i++) {
        const value = target[keys[i]]
        if (value !== undefined && value !== null && value !== '') return value
      }
      return undefined
    },
    formatIndex(index) {
      return String(index + 1).padStart(2, '0')
    },
    formatNumber(value) {
      const num = Number(value)
      if (value === null || value === undefined || value === '' || Number.isNaN(num)) return '--'
      return num.toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    formatPercent(value) {
      const num = Number(value)
      if (value === null || value === undefined || value === '' || Number.isNaN(num)) return '--'
      return num > 0 && num <= 1 ? `${(num * 100).toFixed(1)}%` : `${num.toFixed(1)}%`
    },
    formatCompare(value, prefix) {
      if (value === null || value === undefined || value === '') return `${prefix} --`
      const num = Number(value)
      if (Number.isNaN(num)) return `${prefix} ${value}`
      const sign = num > 0 ? '+' : ''
      return `${prefix} ${sign}${num.toFixed(1)}%`
    },
    formatStay(row) {
      const minutes = this.pick(row, ['plantStayMinutes', 'stayMinutes', 'durationMinutes'])
      if (minutes !== undefined) return `${this.formatNumber(minutes)} 分`
      const seconds = this.pick(row, ['plantStaySeconds', 'staySeconds'])
      if (seconds !== undefined) return `${this.formatNumber(Math.round(Number(seconds) / 60))} 分`
      return '--'
    },
    formatDate(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    formatDateTime(date) {
      const h = String(date.getHours()).padStart(2, '0')
      const m = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${this.formatDate(date)} ${h}:${m}:${s}`
    }
  }
}
</script>

<style scoped lang="scss">
.special-coal-page {
  min-width: 1180px;
  padding: 12px;
  background: #eef3f8;
  color: #1f2937;
}

.filter-panel,
.panel,
.table-panel,
.metric-card {
  border: 1px solid #e5e7eb;
  border-color: #dfe7f1;
  border-radius: 6px;
  background: #fff;
  box-shadow: none;
}

.filter-panel {
  padding: 12px 14px;
  margin-bottom: 12px;
}

.filter-form {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 12px;
  align-items: center;

  ::v-deep .el-form-item {
    margin: 0;
  }

  ::v-deep .el-form-item:not(.filter-actions) {
    width: 236px;
  }

  ::v-deep .el-form-item:first-child {
    width: 390px;
  }

  ::v-deep .el-date-editor,
  ::v-deep .el-select,
  ::v-deep .el-input {
    width: 100%;
  }
}

.filter-actions {
  ::v-deep .el-form-item__content {
    margin-left: 0 !important;
    white-space: nowrap;
  }
}

.quick-range {
  display: flex;
  gap: 6px;
  padding-top: 10px;
  margin-left: 76px;
}

.state-alert {
  margin-bottom: 12px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  min-height: 114px;
  margin-bottom: 12px;
}

.metric-card {
  min-width: 0;
  min-height: 88px;
  padding: 13px 14px;
  overflow: hidden;
}

.metric-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  color: #64748b;
  font-size: 12px;
  line-height: 18px;
}

.metric-head span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-value {
  margin-top: 10px;
  color: #111827;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.15;
  white-space: nowrap;
}

.metric-value small {
  margin-left: 4px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 400;
}

.metric-foot {
  margin-top: 7px;
  overflow: hidden;
  color: #94a3b8;
  font-size: 12px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.accent-blue {
  border-top: 3px solid #409eff;
}

.accent-green {
  border-top: 3px solid #10b981;
}

.accent-orange {
  border-top: 3px solid #f59e0b;
}

.accent-cyan {
  border-top: 3px solid #06b6d4;
}

.accent-red {
  border-top: 3px solid #ef4444;
}

.main-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.42fr) minmax(360px, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}

.rank-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  min-height: 272px;
  margin-bottom: 12px;
}

.panel,
.table-panel {
  padding: 14px;
  overflow: hidden;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 43px;
  margin: -14px -14px 12px;
  padding: 0 14px;
  border-bottom: 1px solid #e5edf6;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  box-sizing: border-box;
}

.panel-head span:last-child {
  overflow: hidden;
  color: #8a97a8;
  font-size: 12px;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chart-box {
  height: 254px;
}

.insight-panel {
  min-height: 310px;
}

.insight-list {
  display: grid;
  gap: 10px;
}

.insight-item {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  gap: 10px;
  padding: 11px 12px;
  border: 1px solid #edf2f7;
  border-radius: 4px;
  background: #fbfdff;
}

.insight-no {
  width: 34px;
  height: 34px;
  border-radius: 4px;
  background: #ecf5ff;
  color: #1d4ed8;
  text-align: center;
  line-height: 34px;
  font-weight: 700;
}

.insight-content {
  min-width: 0;
}

.insight-title {
  overflow: hidden;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.insight-text {
  margin-top: 3px;
  color: #667085;
  font-size: 12px;
  line-height: 18px;
}

::v-deep .rank-list {
  min-height: 210px;
}

::v-deep .rank-row {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) 96px;
  gap: 8px;
  align-items: center;
  min-height: 38px;
  border-bottom: 1px solid #f0f2f5;
}

::v-deep .rank-row:last-child {
  border-bottom: 0;
}

::v-deep .rank-no {
  color: #909399;
  font-weight: 700;
}

::v-deep .rank-name {
  overflow: hidden;
  color: #344054;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::v-deep .rank-value {
  color: #111827;
  font-size: 13px;
  text-align: right;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

::v-deep .rank-progress {
  grid-column: 2 / 4;
  height: 8px;
  margin-top: -5px;
  border-radius: 8px;
  background: #eef2f7;
  overflow: hidden;
}

::v-deep .rank-progress i {
  display: block;
  height: 100%;
  border-radius: 8px;
  background: #409eff;
}

.table-panel {
  margin-bottom: 12px;
}

.table-head {
  margin-bottom: 10px;
}

.table-error {
  margin-bottom: 10px;
}

.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding-top: 10px;
  color: #6b7280;
}

::v-deep .el-table th.el-table__cell {
  padding: 0;
  background: #f6f8fb;
  color: #344054;
  font-weight: 600;
  border-right: none;
}

::v-deep .el-table td.el-table__cell {
  padding: 8px 0;
  border-right: none;
}

::v-deep .el-table::before,
::v-deep .el-table::after {
  display: none;
}

@media (max-width: 1360px) {
  .filter-form {
    gap: 10px;
  }

  .filter-actions {
    ::v-deep .el-form-item__content {
      margin-left: 76px !important;
    }
  }

  .summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
